import React, { useState, useEffect, useRef } from 'react';
import { X, Info, CheckCircle2, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { apiService } from '../connectionsApiService';
import { createDataStore, testDataStore } from '../dataStoreApi';
import { getCategoryLabel, resolveTemplateId } from '../catalog';
import TeamsChannelsPanel, {
    normalizeChannelWebhooks,
    sanitizeWebhooksForSave,
} from './TeamsChannelsPanel';
import RestApiConnectionForm from './RestApiConnectionForm';


interface Props {
    connectorKey: string;
    onClose: () => void;
    onSubmit: (data: any) => void;
    initialData?: any;
}

const SECRET_PLACEHOLDER = '********';
const CIPHER_RE = /^[A-Za-z0-9+/]{24,}={0,2}$/;

function looksLikeCipher(value: unknown): boolean {
    return typeof value === 'string' && CIPHER_RE.test(value.trim());
}

function isSecretAuthKey(key: string): boolean {
    const k = key.toLowerCase();
    return /password|secret|token|apikey|privatekey|certificate|credentialsjson|passphrase/.test(k);
}

function sanitizeAuthForEdit(auth: Record<string, unknown> | undefined): { auth: Record<string, unknown>; maskedKeys: string[] } {
    if (!auth || typeof auth !== 'object') return { auth: {}, maskedKeys: [] };
    const next: Record<string, unknown> = { ...auth };
    const maskedKeys: string[] = [];
    Object.keys(next).forEach((key) => {
        const val = next[key];
        if (typeof val !== 'string' || !val) return;
        if (isSecretAuthKey(key) || looksLikeCipher(val)) {
            next[key] = '';
            maskedKeys.push(key);
        }
    });
    return { auth: next, maskedKeys };
}

function applyKeptSecrets(auth: Record<string, unknown> | undefined, maskedKeys: string[]): Record<string, unknown> {
    const next: Record<string, unknown> = { ...(auth || {}) };
    maskedKeys.forEach((key) => {
        if (!String(next[key] ?? '').trim()) next[key] = SECRET_PLACEHOLDER;
    });
    return next;
}


const initializeFormData = (schema: any) => {
    const data: any = {};
    if (schema.properties) {
        Object.keys(schema.properties).forEach(key => {
            const prop = schema.properties[key];
            if (prop.type === 'object' && prop.properties) {
                data[key] = initializeFormData(prop);
            } else if (prop.default !== undefined) {
                data[key] = prop.default;
            } else if (prop.type === 'string') {
                data[key] = '';
            } else if (prop.type === 'integer') {
                data[key] = prop.default || 0;
            } else if (prop.type === 'boolean') {
                data[key] = prop.default || false;
            } else if (prop.type === 'array') {
                // Respect default values for arrays (deep copy to avoid reference issues)
                data[key] = prop.default ? JSON.parse(JSON.stringify(prop.default)) : [];
            }
        });
    }
    return data;
};

const GenericConnectorForm: React.FC<Props> = (props) => {
    if (resolveTemplateId(props.connectorKey) === 'api_rest') {
        return <RestApiConnectionForm {...props} />;
    }
    return <GenericConnectorFormImpl {...props} />;
};

const GenericConnectorFormImpl: React.FC<Props> = ({ connectorKey, onClose, onSubmit, initialData }) => {

    const [template, setTemplate] = useState<any>(null);
    const [formData, setFormData] = useState<any>({});
    const [errors, setErrors] = useState<any>({});
    const [isTesting, setIsTesting] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
    const [collapsedGroups, setCollapsedGroups] = useState<{ [key: string]: boolean }>({});
    const [isCreating, setIsCreating] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [connectionId, setConnectionId] = useState<string | undefined>(initialData?.id);
    const oauthListenerRef = useRef<((e: MessageEvent) => void) | null>(null);
    const maskedSecretKeysRef = useRef<string[]>([]);

    const isEditing = !!initialData || !!connectionId;

    const isKeptSecret = (path: string, value: unknown): boolean => {
        if (!isEditing) return false;
        const empty = value === undefined || value === null || value === '' || value === SECRET_PLACEHOLDER;
        if (!empty) return false;
        const key = path.split('.').pop() || '';
        return maskedSecretKeysRef.current.includes(key) || (path.startsWith('auth.') && isSecretAuthKey(key));
    };

    useEffect(() => {
        return () => {
            if (oauthListenerRef.current) {
                window.removeEventListener('message', oauthListenerRef.current);
                oauthListenerRef.current = null;
            }
        };
    }, []);
    useEffect(() => {
        const loadTemplate = async () => {
            try {
                let data: any;

                // Resolve aliases / display names / stored keys via catalog (preserves existing connectorKeys)
                const resolvedKey = resolveTemplateId(connectorKey);

                if (resolvedKey === 'postgres') {
                    data = await import('../JSON/pg_sql.json');
                } else if (resolvedKey === 'hana') {
                    data = await import('../JSON/hana.json');
                } else if (resolvedKey === 'mysql') {
                    data = await import('../JSON/my_sql.json');
                } else if (resolvedKey === 'mongodb') {
                    data = await import('../JSON/mongo_db.json');
                } else if (resolvedKey === 'mssql') {
                    data = await import('../JSON/mssql.json');
                } else if (resolvedKey === 'dynamics365') {
                    data = await import('../JSON/dynamics365.json');
                } else if (resolvedKey === 'salesforce') {
                    data = await import('../JSON/salesforce.json');
                } else if (resolvedKey === 'api_rest') {
                    data = await import('../JSON/api_rest.json');
                } else if (resolvedKey === 'snowflake') {
                    data = await import('../JSON/snowflake.json');
                } else if (resolvedKey === 's3') {
                    data = await import('../JSON/s3.json');
                } else if (resolvedKey === 'redis') {
                    data = await import('../JSON/redis.json');
                } else if (resolvedKey === 'elasticsearch') {
                    data = await import('../JSON/elasticsearch.json');
                } else if (resolvedKey === 'oracle') {
                    data = await import('../JSON/oracle.json');
                } else if (resolvedKey === 'ms-team') {
                    data = await import('../JSON/ms-team.json');
                } else if (resolvedKey === 'microsoftoutlook') {
                    data = await import('../JSON/microsoft-outlook.json');
                } else if (resolvedKey === 'slack') {
                    data = await import('../JSON/slack.json');
                } else if (resolvedKey === 'googleworkspace') {
                    data = await import('../JSON/google-workspace.json');
                } else if (resolvedKey === 'google-drive') {
                    data = await import('../JSON/google-drive.json');
                } else if (resolvedKey === 'onedrive') {
                    data = await import('../JSON/onedrive.json');
                } else if (resolvedKey === 'sharepoint') {
                    data = await import('../JSON/sharepoint.json');
                } else if (resolvedKey === 'sftp') {
                    data = await import('../JSON/sftp.json');
                } else if (resolvedKey === 'smtp') {
                    data = await import('../JSON/smtp.json');
                } else if (resolvedKey === 'imap') {
                    data = await import('../JSON/imap.json');
                } else if (resolvedKey === 'whatsapp') {
                    data = await import('../JSON/whatsapp.json');
                } else if (resolvedKey === 'facebook') {
                    data = await import('../JSON/facebook.json');
                } else if (resolvedKey === 'sqlite') {
                    data = await import('../JSON/sqlite.json');
                } else if (resolvedKey === 'firestore') {
                    data = await import('../JSON/firestore.json');
                } else if (resolvedKey === 'azure_blob') {
                    data = await import('../JSON/azure_blob.json');
                } else if (resolvedKey === 'gcs') {
                    data = await import('../JSON/gcs.json');
                } else if (resolvedKey === 'discord') {
                    data = await import('../JSON/discord.json');
                } else if (resolvedKey === 'telegram') {
                    data = await import('../JSON/telegram.json');
                } else if (resolvedKey === 'notion') {
                    data = await import('../JSON/notion.json');
                } else if (resolvedKey === 'confluence') {
                    data = await import('../JSON/confluence.json');
                } else if (resolvedKey === 'hubspot') {
                    data = await import('../JSON/hubspot.json');
                } else if (resolvedKey === 'sap') {
                    data = await import('../JSON/sap.json');
                } else if (resolvedKey === 'google_sheets') {
                    data = await import('../JSON/google_sheets.json');
                } else if (resolvedKey === 'instagram') {
                    data = await import('../JSON/instagram.json');
                } else if (resolvedKey === 'linkedin') {
                    data = await import('../JSON/linkedin.json');
                } else if (resolvedKey === 'twilio') {
                    data = await import('../JSON/twilio.json');
                } else if (resolvedKey === 'webhook') {
                    data = await import('../JSON/webhook.json');
                } else if (resolvedKey === 'graphql') {
                    data = await import('../JSON/graphql.json');
                } else if (resolvedKey === 'servicenow') {
                    data = await import('../JSON/servicenow.json');
                } else if (resolvedKey === 'jira') {
                    data = await import('../JSON/jira.json');
                } else if (resolvedKey === 'databricks') {
                    data = await import('../JSON/databricks.json');
                } else if (resolvedKey === 'entra_id') {
                    data = await import('../JSON/entra_id.json');
                } else if (resolvedKey === 'okta') {
                    data = await import('../JSON/okta.json');
                } else if (resolvedKey === 'netsuite') {
                    data = await import('../JSON/netsuite.json');
                } else if (resolvedKey === 'workday') {
                    data = await import('../JSON/workday.json');
                } else if (resolvedKey === 'oracle_fusion') {
                    data = await import('../JSON/oracle_fusion.json');
                } else if (resolvedKey === 'powerbi') {
                    data = await import('../JSON/powerbi.json');
                } else if (resolvedKey === 'splunk') {
                    data = await import('../JSON/splunk.json');
                } else if (resolvedKey === 'sentinel') {
                    data = await import('../JSON/sentinel.json');
                } else if (resolvedKey === 'palo_alto') {
                    data = await import('../JSON/palo_alto.json');
                } else if (resolvedKey === 'amazon_sp_api') {
                    data = await import('../JSON/amazon_sp_api.json');
                } else {
                    setLoadError(`Connector template for "${connectorKey}" not found.`);
                    return;
                }
                const tField = data.default || data;
                setTemplate(tField);

                // Initialize form data with defaults or initialData
                let initialValues: any;
                if (initialData) {
                    const cfg = initialData.config || {};
                    const sanitized = sanitizeAuthForEdit(initialData.auth);
                    maskedSecretKeysRef.current = sanitized.maskedKeys;
                    initialValues = {
                        name: initialData.name,
                        description: initialData.description,
                        status: initialData.status,
                        environment: initialData.environment,
                        tags: initialData.tags || [],
                        config: {
                            ...cfg,
                            channelWebhooks: normalizeChannelWebhooks(cfg.channelWebhooks),
                        },
                        auth: sanitized.auth,
                    };
                    setConnectionId(initialData.id);
                } else {
                    maskedSecretKeysRef.current = [];
                    initialValues = initializeFormData(tField.schema);
                    if (!initialValues.config || typeof initialValues.config !== 'object') {
                        initialValues.config = {};
                    }
                    setConnectionId(undefined);
                }

                setFormData(initialValues);
                // Initialize collapsed state
                const initialCollapsed: { [key: string]: boolean } = {};
                tField.ui.groups.forEach((group: any) => {
                    if (group.collapsible) {
                        initialCollapsed[group.title] = group.defaultCollapsed !== false;
                    }
                });
                setCollapsedGroups(initialCollapsed);
                setLoadError(null);
            } catch (err: any) {
                console.error('Error loading template:', err);
                setLoadError(`Failed to load template for ${connectorKey}. Please check if the JSON configuration exists.`);
            }
        };

        loadTemplate();
    }, [connectorKey, initialData]);

    const toggleGroup = (title: string) => {
        setCollapsedGroups(prev => ({ ...prev, [title]: !prev[title] }));
    };



    const handleChange = (path: string, value: any) => {
        const keys = path.split('.');
        setFormData((prev: any) => {
            const newData = { ...prev };
            let current = newData;
            for (let i = 0; i < keys.length - 1; i++) {
                current[keys[i]] = { ...current[keys[i]] };
                current = current[keys[i]];
            }
            current[keys[keys.length - 1]] = value;
            return newData;
        });
        // Clear error when field changes
        if (errors[path]) {
            const newErrors = { ...errors };
            delete newErrors[path];
            setErrors(newErrors);
        }
    };

    const getValue = (path: string) => {
        const keys = path.split('.');
        let current = formData;
        for (const key of keys) {
            current = current?.[key];
        }
        return current !== undefined ? current : '';
    };

    const isFieldRequired = (path: string): boolean => {
        if (!template?.schema) return false;
        const parts = path.split('.');
        let currentSchema = template.schema;

        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            const required = (currentSchema as any).required || [];
            if (i === parts.length - 1) {
                if (!required.includes(part)) return false;
                return !isKeptSecret(path, getValue(path));
            }
            if (!currentSchema.properties || !(currentSchema.properties as any)[part]) return false;
            currentSchema = (currentSchema.properties as any)[part];
        }
        return false;
    };

    const validate = () => {
        const newErrors: any = {};
        const rootProperties = template.schema.properties;
        const requiredFields = template.schema.required || [];

        requiredFields.forEach((field: string) => {
            if (!formData[field]) {
                newErrors[field] = 'Required field';
            }
        });

        // Deep validation for dynamic fields
        const checkRequired = (schemaProps: any, currentData: any, prefix = '') => {
            if (!schemaProps) return;

            Object.keys(schemaProps).forEach(key => {
                const prop = schemaProps[key];
                const path = prefix ? `${prefix}.${key}` : key;
                const value = currentData?.[key];

                if (prop.type === 'object' && prop.properties) {
                    const subRequired = prop.required || [];
                    subRequired.forEach((req: string) => {
                        const childPath = `${path}.${req}`;
                        const childVal = value?.[req];
                        if (!childVal && isKeptSecret(childPath, childVal)) return;
                        if (!childVal) {
                            newErrors[childPath] = 'Required field';
                        }
                    });

                    checkRequired(prop.properties, value, path);
                }

                // Port validation
                if (key === 'port' && value) {
                    if (value < 1 || value > 65535) {
                        newErrors[path] = 'Invalid port (1-65535)';
                    }
                }
            });
        };

        checkRequired(rootProperties, formData);

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const buildTeamsPayload = () => {
        const cfg = formData.config || {};
        const prior = normalizeChannelWebhooks(initialData?.config?.channelWebhooks);
        return {
            ...formData,
            config: {
                ...cfg,
                channelWebhooks: sanitizeWebhooksForSave(
                    normalizeChannelWebhooks(cfg.channelWebhooks),
                    prior
                ),
            },
        };
    };

    const handleTest = async () => {
        if (!validate()) return;
        setIsTesting(true);
        setTestResult(null);

        try {
            const payload =
                template.connectorKey === 'microsoftTeams'
                    ? buildTeamsPayload()
                    : formData;
            const data =
                template.connectorKey === 'hana'
                    ? await testDataStore({
                        connectorKey: template.connectorKey,
                        ...payload,
                    })
                    : await apiService.post('/DataStores/test', {
                        connectorKey: template.connectorKey,
                        ...payload
                    }, 'backend');

            setIsTesting(false);
            setTestResult({
                success: data.success,
                message: data.message || (data.success ? 'Connection successful!' : 'Connection failed.')
            });
        } catch (err: any) {
            setIsTesting(false);
            setTestResult({
                success: false,
                message: 'Error connecting to the API server.'
            });
        }
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isCreating) return;

        if (!validate()) return;

        setIsCreating(true);
        setTestResult(null);

        try {
            const isTeams = template.connectorKey === 'microsoftTeams';
            const existingId = initialData?.id || connectionId;
            const body = isTeams ? buildTeamsPayload() : formData;
            const config = body.config && typeof body.config === 'object' ? body.config : {};
            const auth = existingId
                ? applyKeptSecrets(body.auth, maskedSecretKeysRef.current)
                : body.auth;
            const payload = {
                connectorKey: template.connectorKey,
                ...body,
                config,
                auth,
            };
            const data = existingId
                ? await apiService.put(`/DataStores/${existingId}`, payload, 'backend')
                : await createDataStore(payload);

            setIsCreating(false);

            // Teams: after first create, keep form open so user can fetch channels
            if (isTeams && !existingId && data?.id) {
                setConnectionId(data.id);
                setTestResult({
                    success: true,
                    message: 'Connection saved. Fetch teams below, pick a team, then load channels and optional webhooks.',
                });
                onSubmit(data);
                return;
            }

            onSubmit(data);
            onClose();
        } catch (err: any) {
            setIsCreating(false);
            setTestResult({
                success: false,
                message: err.message || 'Error creating connection'
            });
        }
    };

    const handleTeamsChannelsChange = (next: {
        channelWebhooks: Record<string, string>;
        defaultTeamId?: string;
        defaultChannelId?: string;
        defaultWebhookUrl?: string;
    }) => {
        setFormData((prev: any) => ({
            ...prev,
            config: {
                ...prev.config,
                channelWebhooks: next.channelWebhooks,
                ...(next.defaultTeamId !== undefined
                    ? { defaultTeamId: next.defaultTeamId }
                    : {}),
                ...(next.defaultChannelId !== undefined
                    ? { defaultChannelId: next.defaultChannelId }
                    : {}),
                ...(next.defaultWebhookUrl !== undefined
                    ? { defaultWebhookUrl: next.defaultWebhookUrl }
                    : {}),
            },
        }));
    };

    const renderField = (fieldPath: string) => {
        const keys = fieldPath.split('.');
        let schema = template.schema.properties;

        for (let i = 0; i < keys.length - 1; i++) {
            schema = schema[keys[i]]?.properties;
        }

        const fieldSchema = schema[keys[keys.length - 1]];
        const fieldName = keys[keys.length - 1];
        const value = getValue(fieldPath);
        const error = errors[fieldPath];

        const label = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);

        // Conditional Visibility Check
        if (fieldSchema['ui:visibleIf']) {
            const [depKey, depValue] = Object.entries(fieldSchema['ui:visibleIf'])[0] as [string, any];
            const currentValue = getValue(depKey);

            // If dependency value doesn't match, don't render
            // Support array inputs for multiple valid values
            if (Array.isArray(depValue)) {
                if (!depValue.includes(currentValue)) return null;
            } else if (currentValue !== depValue) {
                return null;
            }
        }

        if (fieldSchema.enum) {
            return (
                <div key={fieldPath} className="flex flex-col gap-1.5 text-left">
                    <label className="text-xs font-semibold text-gray-400 flex items-center justify-between gap-1.5 w-full">
                        <div className="flex items-center gap-1.5">
                            {label}
                            {fieldSchema.description && (
                                <span className="group relative">
                                    <Info size={12} className="text-gray-500 cursor-help" />
                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-neutral-800 text-[10px] text-gray-300 rounded shadow-xl border border-neutral-700 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                        {fieldSchema.description}
                                    </span>
                                </span>
                            )}
                        </div>
                        {!isFieldRequired(fieldPath) && (
                            <span className="text-[10px] font-medium text-gray-600 italic bg-neutral-800/50 px-1.5 py-0.5 rounded">Optional</span>
                        )}
                    </label>
                    <select
                        value={value}
                        onChange={(e) => handleChange(fieldPath, e.target.value)}
                        className={`bg-neutral-800 border ${error ? 'border-red-500' : 'border-neutral-700'} rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors appearance-none`}
                    >
                        {fieldSchema.enum.map((opt: string) => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                    {error && <span className="text-[10px] text-red-500">{error}</span>}
                </div>
            );
        }

        if (fieldSchema.type === 'array') {
            const items = Array.isArray(value) ? value : [];
            const isStringArray = fieldSchema.items?.type === 'string';

            return (
                <div key={fieldPath} className="col-span-full space-y-3">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-gray-400 flex items-center gap-1.5">
                            {label}
                            {fieldSchema.description && (
                                <span className="text-[10px] text-gray-500 font-normal">({fieldSchema.description})</span>
                            )}
                        </label>
                        <button
                            type="button"
                            onClick={() => {
                                let newItem: any;
                                if (isStringArray) {
                                    newItem = '';
                                } else {
                                    newItem = {};
                                    const props = fieldSchema.items?.properties || {};
                                    Object.keys(props).forEach(key => {
                                        newItem[key] = props[key].default !== undefined ? props[key].default : (props[key].type === 'integer' ? 0 : '');
                                    });
                                }
                                handleChange(fieldPath, [...items, newItem]);
                            }}
                            className="text-[10px] flex items-center gap-1 bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 px-2 py-1 rounded transition-colors"
                        >
                            <Plus size={10} /> Add {fieldName.slice(0, -1) || 'Item'}
                        </button>
                    </div>

                    <div className="space-y-2">
                        {items.map((item: any, idx: number) => (
                            <div key={idx} className="flex gap-2 items-start bg-neutral-800/50 p-2 rounded-lg border border-neutral-800 group hover:border-neutral-700 transition-colors">
                                <div className="flex-1">
                                    {isStringArray ? (
                                        <input
                                            type="text"
                                            value={item}
                                            onChange={(e) => {
                                                const newItems = [...items];
                                                newItems[idx] = e.target.value;
                                                handleChange(fieldPath, newItems);
                                            }}
                                            className="w-full bg-neutral-900 border border-neutral-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-indigo-500"
                                            placeholder="Enter value"
                                        />
                                    ) : (
                                        <div className="grid grid-cols-2 gap-2">
                                            {fieldSchema.items?.properties && Object.keys(fieldSchema.items.properties).map((propKey: string) => {
                                                const propSchema = fieldSchema.items.properties[propKey];
                                                const itemLabel = propKey.charAt(0).toUpperCase() + propKey.slice(1);
                                                const itemValue = item[propKey];

                                                return (
                                                    <div key={propKey}>
                                                        <label className="text-[10px] text-gray-500 mb-1 block">{itemLabel}</label>
                                                        <input
                                                            type={propSchema.type === 'integer' ? 'number' : 'text'}
                                                            value={itemValue}
                                                            onChange={(e) => {
                                                                const newItems = [...items];
                                                                const val = propSchema.type === 'integer' ? (parseInt(e.target.value) || 0) : e.target.value;
                                                                newItems[idx] = { ...newItems[idx], [propKey]: val };
                                                                handleChange(fieldPath, newItems);
                                                            }}
                                                            className="w-full bg-neutral-900 border border-neutral-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-indigo-500"
                                                            placeholder={itemLabel}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newItems = items.filter((_, i) => i !== idx);
                                        handleChange(fieldPath, newItems);
                                    }}
                                    className="p-1 text-neutral-600 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors mt-1"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                    {items.length === 0 && (
                        <div className="text-center py-4 border border-dashed border-neutral-800 rounded-lg text-xs text-gray-600">
                            No {fieldName} added. Click "+ Add" to configure.
                        </div>
                    )}
                </div>
            );
        }

        if (fieldSchema.type === 'boolean') {
            return (
                <div key={fieldPath} className="flex items-center gap-3 py-2 text-left">
                    <input
                        type="checkbox"
                        checked={!!value}
                        id={fieldPath}
                        onChange={(e) => handleChange(fieldPath, e.target.checked)}
                        className="w-4 h-4 bg-neutral-800 border-neutral-700 rounded text-indigo-600 focus:ring-indigo-500 focus:ring-offset-neutral-900"
                    />
                    <label htmlFor={fieldPath} className="text-sm font-semibold text-gray-300 cursor-pointer">
                        {label}
                        {fieldSchema.description && (
                            <span className="text-xs font-normal text-gray-500 ml-2">({fieldSchema.description})</span>
                        )}
                    </label>
                </div>
            );
        }

        const inputType = fieldSchema['ui:widget'] === 'password' ? 'password' :
            fieldSchema.type === 'integer' ? 'number' : 'text';

        return (
            <div key={fieldPath} className="flex flex-col gap-1.5 text-left">
                <label className="text-xs font-semibold text-gray-400 flex items-center justify-between gap-1.5 w-full">
                    <div className="flex items-center gap-1.5">
                        {label}
                        {fieldSchema.description && (
                            <span className="group relative">
                                <Info size={12} className="text-gray-500 cursor-help" />
                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-neutral-800 text-[10px] text-gray-300 rounded shadow-xl border border-neutral-700 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                    {fieldSchema.description}
                                </span>
                            </span>
                        )}
                    </div>
                    {!isFieldRequired(fieldPath) && (
                        <span className="text-[10px] font-medium text-gray-600 italic bg-neutral-800/50 px-1.5 py-0.5 rounded">Optional</span>
                    )}
                </label>
                <input
                    type={inputType}
                    id={fieldName === 'name' ? 'name-input' : undefined}
                    autoComplete={inputType === 'password' ? 'new-password' : undefined}
                    value={value}
                    onChange={(e) => {
                        const val = inputType === 'number' ? (parseInt(e.target.value) || 0) : e.target.value;
                        handleChange(fieldPath, val);
                    }}
                    className={`bg-neutral-800 border ${error ? 'border-red-500' : 'border-neutral-700'} rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 transition-colors`}
                    placeholder={isEditing && inputType === 'password' ? 'Leave blank to keep current' : label}
                />
                {error && <span className="text-[10px] text-red-500">{error}</span>}
            </div>
        );
    };

    if (loadError) {
        return (
            <div className="portal-content fixed inset-0 z-[110] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
                <div className="bg-neutral-900 border border-red-900/50 rounded-xl p-8 flex flex-col items-center gap-4 max-w-md text-center">
                    <AlertCircle size={40} className="text-red-500" />
                    <h3 className="text-white font-bold text-lg">Loading Error</h3>
                    <p className="text-gray-400 text-sm">{loadError}</p>
                    <button
                        onClick={onClose}
                        className="mt-2 bg-neutral-800 hover:bg-neutral-700 text-white px-6 py-2 rounded-lg transition-colors border border-neutral-700"
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    }

    if (!template) {
        return (
            <div className="portal-content fixed inset-0 z-[110] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
                <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-8 flex flex-col items-center gap-4">
                    <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-400 text-sm">Loading connector template...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="portal-content fixed inset-0 z-[110] bg-black/95 backdrop-blur-md flex items-center justify-center p-4">
            <div className="bg-neutral-900 border border-neutral-800 rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
                {/* Header - Fixed */}
                <div className="flex items-center justify-between p-5 sm:p-6 border-b border-neutral-800 shrink-0">
                    <div className="flex-1 pr-4">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <h2 className="text-lg sm:text-xl font-bold text-white leading-tight">
                                {isEditing ? 'Edit' : template.displayName} Connection
                            </h2>
                            <span className="px-2 py-0.5 bg-indigo-500/10 text-indigo-400 text-[10px] font-bold uppercase tracking-wider rounded border border-indigo-500/20">
                                {getCategoryLabel(template.category)}
                            </span>
                        </div>
                        <p className="text-gray-400 text-[11px] sm:text-xs mt-1">Configure your {template.displayName} integration details below.</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-neutral-800 rounded-lg text-gray-400 hover:text-white transition-colors shrink-0">
                        <X size={20} />
                    </button>
                </div>

                {/* Body - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6 sm:p-10 custom-scrollbar">
                    <div className="max-w-xl mx-auto">
                        <form id="connector-form" onSubmit={handleFormSubmit} className="space-y-12">
                            {template.connectorKey === 'google-drive' && (
                                <div className="space-y-6">
                                    <div className="flex items-center gap-4">
                                        <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] whitespace-nowrap">
                                            Authentication
                                        </h3>
                                        <div className="h-px w-full bg-neutral-800/60"></div>
                                    </div>

                                    <div className="p-6 bg-neutral-800/20 border border-neutral-800 rounded-2xl overflow-hidden relative">
                                        {/* Background decoration */}
                                        <div className="absolute top-0 right-0 p-4 opacity-5">
                                            <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                                            </svg>
                                        </div>

                                        <div className="flex flex-col items-center text-center space-y-4">
                                            {testResult?.success ? (
                                                <>
                                                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 border border-emerald-500/30 animate-in zoom-in duration-300">
                                                        <CheckCircle2 size={32} />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-white font-semibold">Account Connected</h4>
                                                        <p className="text-xs text-gray-400 mt-1">Google Drive is ready for import.</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData((p: any) => ({ ...p, auth: { ...p.auth, code: null, refreshToken: null } }));
                                                            setTestResult(null);
                                                        }}
                                                        className="text-[10px] text-gray-500 hover:text-white transition-colors uppercase tracking-wider font-bold"
                                                    >
                                                        Change Account
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="w-16 h-16 bg-indigo-500/10 rounded-full flex items-center justify-center text-indigo-400 border border-indigo-500/20">
                                                        <svg width="32" height="32" viewBox="0 0 48 48">
                                                            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                                                            <path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z" />
                                                            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                                                            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h4 className="text-white font-semibold">Google Drive Connection</h4>
                                                        <p className="text-xs text-gray-400 mt-1">Connect your account to browse and import your cloud files.</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        disabled={isTesting}
                                                        onClick={() => {
                                                            const clientId = "321440801588-kie14jgip81ericocrkfl2mf9vk6lkme.apps.googleusercontent.com";
                                                            const redirectUri = window.location.origin + "/auth/callback";
                                                            const scope = "https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile";
                                                            const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent`;

                                                            const width = 500;
                                                            const height = 600;
                                                            const left = window.screen.width / 2 - width / 2;
                                                            const top = window.screen.height / 2 - height / 2;

                                                            const popup = window.open(
                                                                authUrl,
                                                                "GoogleAuth",
                                                                `width=${width},height=${height},top=${top},left=${left}`
                                                            );

                                                            if (popup) {
                                                                if (oauthListenerRef.current) {
                                                                    window.removeEventListener('message', oauthListenerRef.current);
                                                                }
                                                                const handleMessage = (event: MessageEvent) => {
                                                                    if (event.origin !== window.location.origin) return;

                                                                    const { type, payload } = event.data;
                                                                    if (type === 'OAUTH_RESPONSE') {
                                                                        window.removeEventListener('message', handleMessage);
                                                                        oauthListenerRef.current = null;
                                                                        if (payload.code) {
                                                                            setFormData((prev: any) => ({
                                                                                ...prev,
                                                                                auth: {
                                                                                    ...prev.auth,
                                                                                    code: payload.code,
                                                                                    redirectUri: redirectUri
                                                                                }
                                                                            }));

                                                                            setIsTesting(true);
                                                                            setTestResult(null);

                                                                            const testPayload = {
                                                                                connectorKey: template.connectorKey,
                                                                                name: (document.getElementById('name-input') as HTMLInputElement)?.value || "Google Drive Connection",
                                                                                auth: {
                                                                                    ...formData.auth,
                                                                                    token: null,
                                                                                    refreshToken: null,
                                                                                    type: 'oauth2',
                                                                                    clientId: clientId,
                                                                                    code: payload.code,
                                                                                    redirectUri: redirectUri,
                                                                                    scope: scope
                                                                                }
                                                                            };

                                                                            apiService.post('/DataStores/test', testPayload, 'backend')
                                                                                .then((data: any) => {
                                                                                    setIsTesting(false);
                                                                                    if (data.success) {
                                                                                        setTestResult({
                                                                                            success: true,
                                                                                            message: '✅ Google Account Linked Successfully!'
                                                                                        });
                                                                                        if (data.data) {
                                                                                            setFormData((prev: any) => ({
                                                                                                ...prev,
                                                                                                auth: {
                                                                                                    ...prev.auth,
                                                                                                    ...data.data,
                                                                                                    code: null
                                                                                                }
                                                                                            }));
                                                                                        }
                                                                                    } else {
                                                                                        setTestResult({
                                                                                            success: false,
                                                                                            message: '❌ Verification failed: ' + (data.message || 'Unknown error')
                                                                                        });
                                                                                    }
                                                                                })
                                                                                .catch((err: any) => {
                                                                                    setIsTesting(false);
                                                                                    setTestResult({
                                                                                        success: false,
                                                                                        message: '❌ Connection failed: ' + (err.response?.data?.message || err.message)
                                                                                    });
                                                                                });
                                                                        } else if (payload.error) {
                                                                            const errorMsg = payload.error === 'access_denied'
                                                                                ? 'Access denied. Please approve the permissions.'
                                                                                : `Authentication failed: ${payload.error}`;
                                                                            setTestResult({ success: false, message: '⚠️ ' + errorMsg });
                                                                        }
                                                                    }
                                                                };
                                                                oauthListenerRef.current = handleMessage;
                                                                window.addEventListener('message', handleMessage);
                                                            }
                                                        }}
                                                        className={`w-full max-w-xs py-3 px-6 rounded-xl font-bold flex items-center justify-center gap-3 transition-all ${isTesting
                                                            ? 'bg-neutral-700 text-gray-400 cursor-not-allowed'
                                                            : 'bg-white text-black hover:bg-gray-200 active:scale-[0.98]'
                                                            }`}
                                                    >
                                                        {isTesting ? (
                                                            <>
                                                                <div className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
                                                                Verifying...
                                                            </>
                                                        ) : (
                                                            <>
                                                                Continue with Google
                                                            </>
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                        {/* Result Message Box */}
                                        {testResult && !testResult.success && template.connectorKey === 'google-drive' && (
                                            <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                                                <p className="text-xs text-red-400 text-center leading-relaxed font-medium">
                                                    {testResult.message}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {template.connectorKey === 'onedrive' && (
                                <div className="space-y-6">
                                    <div className="flex items-center gap-4">
                                        <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] whitespace-nowrap">
                                            Authentication
                                        </h3>
                                        <div className="h-px w-full bg-neutral-800/60"></div>
                                    </div>

                                    <div className="p-6 bg-neutral-800/20 border border-neutral-800 rounded-2xl overflow-hidden relative">
                                        <div className="absolute top-0 right-0 p-4 opacity-5">
                                            <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                                            </svg>
                                        </div>

                                        <div className="flex flex-col items-center text-center space-y-4">
                                            {testResult?.success ? (
                                                <>
                                                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 border border-emerald-500/30 animate-in zoom-in duration-300">
                                                        <CheckCircle2 size={32} />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-white font-semibold">Account Connected</h4>
                                                        <p className="text-xs text-gray-400 mt-1">OneDrive is ready for import.</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData((p: any) => ({ ...p, auth: { ...p.auth, code: null, refreshToken: null } }));
                                                            setTestResult(null);
                                                        }}
                                                        className="text-[10px] text-gray-500 hover:text-white transition-colors uppercase tracking-wider font-bold"
                                                    >
                                                        Change Account
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center text-blue-400 border border-blue-500/20">
                                                        <svg width="32" height="32" viewBox="0 0 23 23">
                                                            <path fill="#f35325" d="M1 1h10v10H1z" />
                                                            <path fill="#81bc06" d="M12 1h10v10H12z" />
                                                            <path fill="#05a6f0" d="M1 12h10v10H1z" />
                                                            <path fill="#ffba08" d="M12 12h10v10H12z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h4 className="text-white font-semibold">OneDrive Connection</h4>
                                                        <p className="text-xs text-gray-400 mt-1">Connect your Microsoft account to browse and import your cloud files.</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        disabled={isTesting}
                                                        onClick={() => {
                                                            const clientId = "ea648125-6eb8-4d7e-92df-992840041962";
                                                            const redirectUri = window.location.origin + "/auth/callback";
                                                            const scope = "Files.Read.All Files.ReadWrite.All User.Read offline_access";
                                                            const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&response_mode=query&prompt=consent`;

                                                            const width = 500;
                                                            const height = 650;
                                                            const left = window.screen.width / 2 - width / 2;
                                                            const top = window.screen.height / 2 - height / 2;

                                                            const popup = window.open(
                                                                authUrl,
                                                                "MicrosoftAuth",
                                                                `width=${width},height=${height},top=${top},left=${left}`
                                                            );

                                                            if (popup) {
                                                                if (oauthListenerRef.current) {
                                                                    window.removeEventListener('message', oauthListenerRef.current);
                                                                }
                                                                const handleMessage = (event: MessageEvent) => {
                                                                    if (event.origin !== window.location.origin) return;

                                                                    const { type, payload } = event.data;
                                                                    if (type === 'OAUTH_RESPONSE') {
                                                                        window.removeEventListener('message', handleMessage);
                                                                        oauthListenerRef.current = null;
                                                                        if (payload.code) {
                                                                            setFormData((prev: any) => ({
                                                                                ...prev,
                                                                                auth: {
                                                                                    ...prev.auth,
                                                                                    code: payload.code,
                                                                                    redirectUri: redirectUri
                                                                                }
                                                                            }));

                                                                            setIsTesting(true);
                                                                            setTestResult(null);

                                                                            const testPayload = {
                                                                                connectorKey: template.connectorKey,
                                                                                name: (document.getElementById('name-input') as HTMLInputElement)?.value || "OneDrive Connection",
                                                                                auth: {
                                                                                    ...formData.auth,
                                                                                    token: null,
                                                                                    refreshToken: null,
                                                                                    type: 'oauth2',
                                                                                    clientId: clientId,
                                                                                    code: payload.code,
                                                                                    redirectUri: redirectUri,
                                                                                    scope: scope
                                                                                }
                                                                            };

                                                                            apiService.post('/DataStores/test', testPayload, 'backend')
                                                                                .then((data: any) => {
                                                                                    setIsTesting(false);
                                                                                    if (data.success) {
                                                                                        setTestResult({
                                                                                            success: true,
                                                                                            message: 'Microsoft Account Linked Successfully!'
                                                                                        });
                                                                                        if (data.data) {
                                                                                            setFormData((prev: any) => ({
                                                                                                ...prev,
                                                                                                auth: {
                                                                                                    ...prev.auth,
                                                                                                    ...data.data,
                                                                                                    code: null
                                                                                                }
                                                                                            }));
                                                                                        }
                                                                                    } else {
                                                                                        setTestResult({
                                                                                            success: false,
                                                                                            message: 'Verification failed: ' + (data.message || 'Unknown error')
                                                                                        });
                                                                                    }
                                                                                })
                                                                                .catch((err: any) => {
                                                                                    setIsTesting(false);
                                                                                    setTestResult({
                                                                                        success: false,
                                                                                        message: 'Connection failed: ' + (err.response?.data?.message || err.message)
                                                                                    });
                                                                                });
                                                                        } else if (payload.error) {
                                                                            const errorMsg = payload.error === 'access_denied'
                                                                                ? 'Access denied. Please approve the permissions.'
                                                                                : `Authentication failed: ${payload.error}`;
                                                                            setTestResult({ success: false, message: errorMsg });
                                                                        }
                                                                    }
                                                                };
                                                                oauthListenerRef.current = handleMessage;
                                                                window.addEventListener('message', handleMessage);
                                                            }
                                                        }}
                                                        className={`w-full max-w-xs py-3 px-6 rounded-xl font-bold flex items-center justify-center gap-3 transition-all ${isTesting
                                                            ? 'bg-neutral-700 text-gray-400 cursor-not-allowed'
                                                            : 'bg-[#2f2f2f] text-white hover:bg-[#3b3b3b] active:scale-[0.98] border border-neutral-600'
                                                            }`}
                                                    >
                                                        {isTesting ? (
                                                            <>
                                                                <div className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
                                                                Verifying...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg width="20" height="20" viewBox="0 0 23 23">
                                                                    <path fill="#f35325" d="M1 1h10v10H1z" />
                                                                    <path fill="#81bc06" d="M12 1h10v10H12z" />
                                                                    <path fill="#05a6f0" d="M1 12h10v10H1z" />
                                                                    <path fill="#ffba08" d="M12 12h10v10H12z" />
                                                                </svg>
                                                                Continue with Microsoft
                                                            </>
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                        {testResult && !testResult.success && template.connectorKey === 'onedrive' && (
                                            <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                                                <p className="text-xs text-red-400 text-center leading-relaxed font-medium">
                                                    {testResult.message}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {template.ui.groups.map((group: any, idx: number) => {
                                const isCollapsed = collapsedGroups[group.title];
                                const isChannelDefaults =
                                    template.connectorKey === 'microsoftTeams' &&
                                    group.title === '3. Channel Delivery Defaults';
                                return (
                                    <div key={idx} className="space-y-6">
                                        <div
                                            className={`flex items-center gap-4 ${group.collapsible ? 'cursor-pointer group/header' : ''}`}
                                            onClick={() => group.collapsible && toggleGroup(group.title)}
                                        >
                                            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] whitespace-nowrap">
                                                {group.title}
                                            </h3>
                                            <div className="h-px w-full bg-neutral-800/60"></div>
                                            {group.collapsible && (
                                                <div className="text-gray-500 group-hover/header:text-indigo-400 transition-colors">
                                                    <svg
                                                        className={`w-4 h-4 transition-transform duration-300 ${isCollapsed ? '' : 'rotate-180'}`}
                                                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>

                                        {!isCollapsed && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 animate-in fade-in slide-in-from-top-4 duration-500">
                                                {group.fields.map((fieldPath: string) => renderField(fieldPath))}
                                            </div>
                                        )}

                                        {isChannelDefaults && !isCollapsed && (
                                            <TeamsChannelsPanel
                                                datastoreId={connectionId || initialData?.id}
                                                defaultTeamId={formData.config?.defaultTeamId || formData.config?.teamId}
                                                defaultChannelId={formData.config?.defaultChannelId}
                                                defaultWebhookUrl={formData.config?.defaultWebhookUrl}
                                                channelWebhooks={formData.config?.channelWebhooks}
                                                onChange={handleTeamsChannelsChange}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </form>
                    </div>
                </div>

                {/* Connection Test Result - Fixed above footer */}
                {testResult && (
                    <div className="px-6 py-2 shrink-0 animate-in fade-in slide-in-from-bottom-3 duration-500">
                        <div className={`group relative p-4 rounded-xl border-2 flex items-start gap-4 shadow-2xl ${testResult.success
                            ? 'bg-indigo-500/5 border-indigo-500/20 text-indigo-300'
                            : 'bg-red-500/5 border-red-500/20 text-red-400'
                            }`}>
                            <div className={`mt-0.5 p-2 rounded-lg shrink-0 ${testResult.success ? 'bg-indigo-500/10 text-indigo-400' : 'bg-red-500/10 text-red-500'}`}>
                                {testResult.success ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-[11px] font-black uppercase tracking-[0.15em] mb-1 opacity-60">
                                    {testResult.success ? 'Connection Verified' : 'Connection Failed'}
                                </h4>
                                <p className="text-[13px] leading-relaxed font-medium">
                                    {testResult.message}
                                </p>
                            </div>
                            <button
                                onClick={() => setTestResult(null)}
                                className="mt-0.5 p-1.5 hover:bg-white/5 rounded-lg transition-all opacity-0 group-hover:opacity-100 active:scale-90"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Footer - Fixed */}
                <div className="p-5 sm:p-6 bg-neutral-950/50 border-t border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0 rounded-b-2xl">
                    {template.connectorKey !== 'google-drive' && template.connectorKey !== 'onedrive' && (
                        <button
                            type="button"
                            onClick={handleTest}
                            disabled={isTesting}
                            className="w-full sm:w-auto px-4 py-2 text-sm font-semibold text-gray-300 hover:text-white flex items-center justify-center gap-2 disabled:opacity-50 border border-neutral-800 sm:border-none rounded-lg transition-colors"
                        >
                            {isTesting ? (
                                <div className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin"></div>
                            ) : null}
                            Test Connection
                        </button>
                    )}
                    <div className="flex flex-col-reverse sm:flex-row w-full sm:w-auto gap-3">
                        <button
                            onClick={onClose}
                            className="w-full sm:w-auto px-4 py-2 text-sm font-semibold text-gray-400 hover:text-white transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            form="connector-form"
                            type="submit"
                            disabled={!testResult?.success || isCreating}
                            className={`w-full sm:w-auto px-8 py-2.5 rounded-xl text-sm font-bold shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 ${!testResult?.success ? 'bg-neutral-800 text-gray-500 cursor-not-allowed shadow-none' : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-500/20'}`}
                        >
                            {isCreating && (
                                <div className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin"></div>
                            )}
                            {isEditing ? 'Save Changes' : 'Create Connection'}
                        </button>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default GenericConnectorForm;
